/**
 * Copy for the account-state strips that share the trial banner slot:
 * read-only after cancel (ink) and payment failed (amber). Paper "03 - App ·
 * Paywall & billing": Funnels · Read-only after cancel, Home · Payment failed.
 *
 * Dates and amounts are optional because `/billing/status` doesn't carry them
 * yet; each missing value drops its clause rather than being guessed.
 */
import { formatShortDate } from '@/lib/constants/pricing'

/** What "new … need an active plan" names on the current page. */
export function creationNounForPath(pathname: string): string | null {
  if (/\/funnels?(\/|$)/.test(pathname)) return 'funnels'
  if (/\/heatmaps?(\/|$)/.test(pathname)) return 'heatmaps'
  if (/\/(split-testing|experiments|ab-tests?|test-planner)(\/|$)/.test(pathname)) return 'A/B tests'
  if (/\/api(\/|$)/.test(pathname)) return 'API keys'
  return null
}

export function buildReadOnlyCopy({
  planName,
  endedAt,
  noun,
  needsPayment,
  monthlyPrice,
}: {
  planName: string
  endedAt?: Date | string | null
  noun?: string | null
  /** `unpaid`: the invoice is still open, so the CTA is the portal, not a new checkout. */
  needsPayment: boolean
  monthlyPrice: number
}): { chip: string; message: string; ctaLabel: string } {
  if (needsPayment) {
    return {
      chip: 'Read-only',
      message: `Your ${planName} plan is paused after a failed payment. Everything collected stays readable. Update your card to start tracking again.`,
      ctaLabel: 'Update payment',
    }
  }
  const ended = formatShortDate(endedAt)
  const creation = noun ? `new ${noun} need an active plan` : 'anything new needs an active plan'
  const message = ended
    ? `Your ${planName} plan ended on ${ended}. Everything collected up to ${ended} stays readable. Tracking stopped that day, and ${creation}.`
    : `Your ${planName} plan has ended. Everything collected while it was active stays readable. Tracking has stopped, and ${creation}.`
  return { chip: 'Read-only', message, ctaLabel: `Resubscribe · $${monthlyPrice}/mo` }
}

export function buildPaymentFailedCopy({
  amount,
  failedAt,
  retryAt,
}: {
  amount?: number | null
  failedAt?: Date | string | null
  retryAt?: Date | string | null
}): { chip: string; message: string; ctaLabel: string; hint: string } {
  const failed = formatShortDate(failedAt)
  const retry = formatShortDate(retryAt)
  const what = amount != null ? `Your $${amount} payment` : 'Your last payment'
  const lead = failed ? `${what} on ${failed} failed.` : `${what} failed.`
  const retryLine = retry ? ` Stripe retries on ${retry}.` : ' Stripe will retry it.'
  return {
    chip: 'Payment failed',
    message: `${lead}${retryLine} Update your card so tracking never stops.`,
    ctaLabel: 'Update payment',
    hint: 'Opens Stripe billing',
  }
}
